import {
  ScrollManager,
  ScrollConfig,
  VirtualScrollConfig,
  InfiniteScrollManager,
  VirtualScrollManager,
  ScrollLoadingCallback,
} from './managers';
import { ResultQueriedByCursor } from '@services/MarppleShop/features/Funding';
import { CSSSize } from '../css-unit-parser.ts';

/**
 * 스크롤 매니저 공통 옵셔널 메서드
 */
interface ScrollManagerOptionalMethods<D, C> {
  items(items: D[]): this;
  hasMore(hasMore: boolean): this;
  threshold(threshold: number): this;
  thresholdPercentage(percentage: number): this;
  debounce(debounce: number): this;
  loading(callback: ScrollLoadingCallback): this;
  onError(callback: (error: Error) => void | Promise<void>): this;
  onEnd(callback: () => void | Promise<void>): this;
  build(): ScrollManager<D, C>;
}

/**
 * 가상 스크롤 옵셔널 메서드
 */
interface VirtualScrollManagerOptionalMethods {
  gap(gap: number): this;
  overscan(count: number): this;
}

/**
 * 노멀 모드 선택 후 첫 번째 필수 메서드
 */
class NormalScrollManagerContainerBuilder<D, C> {
  constructor(private config: Partial<ScrollConfig<D, C>>) {}

  container(container: HTMLElement | string): NormalScrollManagerCursorBuilder<D, C> {
    this.config.container = container;
    return new NormalScrollManagerCursorBuilder<D, C>(this.config);
  }
}

/**
 * 가상 스크롤 모드 선택 후 첫 번째 필수 메서드
 */
class VirtualScrollManagerContainerBuilder<D, C> {
  constructor(private config: Partial<ScrollConfig<D, C>>) {}

  container(container: HTMLElement | string): VirtualScrollManagerCursorBuilder<D, C> {
    this.config.container = container;
    return new VirtualScrollManagerCursorBuilder<D, C>(this.config);
  }
}

/**
 * 노멀 모드 커서 설정
 */
class NormalScrollManagerCursorBuilder<D, C> {
  constructor(private config: Partial<ScrollConfig<D, C>>) {}

  cursor(cursor: C): NormalScrollManagerRenderBuilder<D, C> {
    this.config.cursor = cursor;
    return new NormalScrollManagerRenderBuilder<D, C>(this.config);
  }
}

/**
 * 가상 스크롤 모드 커서 설정
 */
class VirtualScrollManagerCursorBuilder<D, C> {
  constructor(private config: Partial<ScrollConfig<D, C>>) {}

  cursor(cursor: C): VirtualScrollManagerRenderBuilder<D, C> {
    this.config.cursor = cursor;
    return new VirtualScrollManagerRenderBuilder<D, C>(this.config);
  }
}

/**
 * 노멀 모드 렌더 설정
 */
class NormalScrollManagerRenderBuilder<D, C> {
  constructor(private config: Partial<ScrollConfig<D, C>>) {}

  render(callback: (result: D[]) => Element[]): NormalScrollManagerOptionalBuilder<D, C> {
    this.config.onRender = callback;
    return new NormalScrollManagerOptionalBuilder<D, C>(this.config);
  }
}

/**
 * 가상 스크롤 모드 렌더 설정
 */
class VirtualScrollManagerRenderBuilder<D, C> {
  constructor(private config: Partial<ScrollConfig<D, C>>) {}

  render(callback: (result: D[]) => Element[]): VirtualScrollManagerHeightBuilder<D, C> {
    this.config.onRender = callback;
    return new VirtualScrollManagerHeightBuilder<D, C>(this.config);
  }
}

/**
 * 가상 스크롤 모드에서만 필요한 height 설정
 */
class VirtualScrollManagerHeightBuilder<D, C> {
  constructor(private config: Partial<ScrollConfig<D, C>>) {}

  /**
   * @param container
   * 가상 스크롤의 부모 요소의 크기를 설정합니다.
   *
   * @param item
   * 가상 스크롤의 자식 아이템들의 크기를 설정합니다.
   */
  height({ container, item }: { container: CSSSize; item: CSSSize | ((item: D) => CSSSize) }) {
    (this.config as VirtualScrollConfig<D, C>).containerHeight = container;
    (this.config as VirtualScrollConfig<D, C>).itemHeight = item;
    return new VirtualScrollManagerOptionalBuilder<D, C>(this.config);
  }
}

/**
 * 노멀 모드 옵셔널 메서드 설정
 */
class NormalScrollManagerOptionalBuilder<D, C> implements ScrollManagerOptionalMethods<D, C> {
  constructor(private config: Partial<ScrollConfig<D, C>>) {}

  items(items: D[]): this {
    this.config.items = items;
    return this;
  }

  hasMore(hasMore: boolean): this {
    this.config.hasMore = hasMore;
    return this;
  }

  threshold(threshold: number): this {
    this.config.threshold = threshold;
    return this;
  }

  thresholdPercentage(percentage: number): this {
    this.config.thresholdPercentage = percentage;
    return this;
  }

  debounce(debounce: number): this {
    this.config.debounce = debounce;
    return this;
  }

  loading(callback: ScrollLoadingCallback): this {
    this.config.loading = callback;
    return this;
  }

  onError(callback: (error: Error) => void | Promise<void>): this {
    this.config.onError = callback;
    return this;
  }

  onEnd(callback: () => void | Promise<void>): this {
    this.config.onEnd = callback;
    return this;
  }

  build(): ScrollManager<D, C> {
    this.validateBuilder();
    const config = this.config as ScrollConfig<D, C>;
    return new InfiniteScrollManager<D, C>(config);
  }

  private validateBuilder(): void {
    if (!this.isBrowser()) {
      throw new Error('ScrollManager is used only browser');
    }

    if (!this.config.container) {
      throw new Error("ScrollManager's Container is required");
    }

    if (!this.config.loader) {
      throw new Error("ScrollManager's Loader function is required");
    }

    if (this.config.cursor === undefined) {
      throw new Error("ScrollManager's Initial cursor is required");
    }

    if (!this.config.onRender) {
      throw new Error("ScrollManager's onRender function is required");
    }
  }

  private isBrowser() {
    return typeof window !== 'undefined' && typeof document !== 'undefined' && globalThis === window;
  }
}

/**
 * 가상 스크롤 모드 옵셔널 메서드 설정
 */
class VirtualScrollManagerOptionalBuilder<D, C>
  implements ScrollManagerOptionalMethods<D, C>, VirtualScrollManagerOptionalMethods
{
  constructor(private config: Partial<ScrollConfig<D, C>>) {}

  gap(gap: CSSSize): this {
    (this.config as VirtualScrollConfig<D, C>).gap = gap;
    return this;
  }

  overscan(count: number): this {
    (this.config as VirtualScrollConfig<D, C>).overscan = count;
    return this;
  }

  responsive(): this {
    (this.config as VirtualScrollConfig<D, C>).responsive = true;
    return this;
  }

  klass({ container, item }: { container?: string; item?: string }): this {
    (this.config as VirtualScrollConfig<D, C>).containerKlass = container;
    (this.config as VirtualScrollConfig<D, C>).itemKlass = item;

    return this;
  }

  items(items: D[]): this {
    this.config.items = items;
    return this;
  }

  hasMore(hasMore: boolean): this {
    this.config.hasMore = hasMore;
    return this;
  }

  threshold(threshold: number): this {
    this.config.threshold = threshold;
    return this;
  }

  thresholdPercentage(percentage: number): this {
    this.config.thresholdPercentage = percentage;
    return this;
  }

  debounce(debounce: number): this {
    this.config.debounce = debounce;
    return this;
  }

  loading(callback: ScrollLoadingCallback): this {
    this.config.loading = callback;
    return this;
  }

  onError(callback: (error: Error) => void | Promise<void>): this {
    this.config.onError = callback;
    return this;
  }

  onEnd(callback: () => void | Promise<void>): this {
    this.config.onEnd = callback;
    return this;
  }

  build(): ScrollManager<D, C> {
    this.validateBuilder();
    const config = this.config as VirtualScrollConfig<D, C>;
    return new VirtualScrollManager<D, C>(config);
  }

  private validateBuilder(): void {
    if (!this.isBrowser()) {
      throw new Error('ScrollManager is used only browser');
    }

    if (!this.config.container) {
      throw new Error("ScrollManager's Container is required");
    }

    if (!this.config.loader) {
      throw new Error("ScrollManager's Loader function is required");
    }

    if (this.config.cursor === undefined) {
      throw new Error("ScrollManager's Initial cursor is required");
    }

    if (!this.config.onRender) {
      throw new Error("ScrollManager's onRender function is required");
    }

    const virtualConfig = this.config as VirtualScrollConfig<D, C>;

    if (!virtualConfig.itemHeight) {
      throw new Error("VirtualScrollManager's itemHeight is required");
    }

    if (!virtualConfig.containerHeight) {
      throw new Error("VirtualScrollManager's containerHeight is required");
    }
  }

  private isBrowser() {
    return typeof window !== 'undefined' && typeof document !== 'undefined' && globalThis === window;
  }
}

/**
 * 스크롤 매니저 모드 선택 클래스
 */
class ScrollManagerModeSelector<D, C> {
  constructor(private config: Partial<ScrollConfig<D, C>>) {}

  normal(): NormalScrollManagerContainerBuilder<D, C> {
    (this.config as ScrollConfig<D, C>).virtual = false;
    return new NormalScrollManagerContainerBuilder<D, C>(this.config);
  }

  virtual(): VirtualScrollManagerContainerBuilder<D, C> {
    (this.config as VirtualScrollConfig<D, C>).virtual = true;
    return new VirtualScrollManagerContainerBuilder<D, C>(this.config);
  }
}

/**
 * 초기 스크롤 매니저 빌더
 */
interface ScrollManagerInitialBuilder {
  loader<C, D>(
    loader: (cursor: C) => Promise<ResultQueriedByCursor<D[], C>>,
  ): ScrollManagerModeSelector<D, C>;
}

/**
 * 스크롤 매니저 빌더 클래스
 */
export class ScrollManagerBuilder implements ScrollManagerInitialBuilder {
  loader<C, D>(
    loader: (cursor: C) => Promise<ResultQueriedByCursor<D[], C>>,
  ): ScrollManagerModeSelector<D, C> {
    const config: Partial<ScrollConfig<D, C>> = { loader };
    return new ScrollManagerModeSelector<D, C>(config);
  }

  static create(): ScrollManagerBuilder {
    return new ScrollManagerBuilder();
  }
}
